import { useEffect } from 'react';
import type { AgentLaneState } from './agentConfig';

type Setter = React.Dispatch<React.SetStateAction<Record<string, AgentLaneState>>>;

interface CreepOptions {
  /** Upper bound progress may creep toward without an explicit event. */
  cap?: number;
  /** Fraction of remaining gap closed each tick. */
  rate?: number;
  /** Floor nudge per tick so motion is visible even near the cap. */
  minNudge?: number;
  /** Tick interval in ms. */
  intervalMs?: number;
}

/**
 * Gently advances `progress` on every lane in `running` phase so the UI never
 * feels frozen between step events. Never reaches `cap` — only an explicit
 * `completed`/`done` event should push to 100%.
 */
export function useLaneCreep(setLaneStates: Setter, opts: CreepOptions = {}) {
  const { cap = 0.92, rate = 0.007, minNudge = 0.001, intervalMs = 600 } = opts;

  useEffect(() => {
    const id = setInterval(() => {
      setLaneStates(prev => {
        let changed = false;
        const next: Record<string, AgentLaneState> = { ...prev };
        for (const k of Object.keys(prev)) {
          const s = prev[k];
          if (s.phase !== 'running') continue;
          const laneCap = typeof s.cap === 'number' ? s.cap : cap;
          if (s.progress >= laneCap) continue;
          const remaining = laneCap - s.progress;
          const nudge = Math.max(minNudge, remaining * rate);
          next[k] = { ...s, progress: Math.min(laneCap, s.progress + nudge) };
          changed = true;
        }
        return changed ? next : prev;
      });
    }, intervalMs);
    return () => clearInterval(id);
  }, [setLaneStates, cap, rate, minNudge, intervalMs]);
}
